import { FC, createContext, useContext } from 'react';
import { usePrequel, usePrequelContext, usePrequelAuth } from './context';
import { PrequelContextProvider } from './provider';
import {
  GetDestinationsFunction,
  UseGetTransfersFunction,
  UseDestinationFormFunction,
  UseEditDestinationFormFunction,
  UseDestinationFormResult,
  UseEditDestinationFormResult,
  VendorName,
} from './type';
import React from 'react';
import { PREQUEL_HOST } from 'modules/dataSync/constants/prequel';

// Configuration constants
const ORIGIN = typeof window !== 'undefined' ? window.origin : '';
const PREQUEL_ORG_ID = '4b0b6f8d-56c3-414e-8755-cfd0f1cd51d7';

interface PrequelHooksContextState {
  getDestinations: GetDestinationsFunction;
  useGetTransfers: UseGetTransfersFunction;
  useDestinationForm: UseDestinationFormFunction;
  useEditDestinationForm: UseEditDestinationFormFunction;
}

interface PrequelHooksProviderProps {
  children: React.ReactNode;
}

// Export initial states for use in mocks and tests
export const initialGetDestinationsState: GetDestinationsFunction = () => Promise.resolve([]);

export const initialGetTransfersState = {
  transfers: [],
  isLoading: true,
  error: null,
};

export const initialDestinationFormState: UseDestinationFormResult = {
  formSections: [],
  destination: {} as any,
  setDestination: () => {},
  testConnection: () =>
    Promise.resolve({ status: 'error' as const, data: null, message: 'Not ready' }),
  getPreparedDestination: () => null,
  isLoading: true,
  error: null,
};

export const initialEditDestinationFormState: UseEditDestinationFormResult = {
  formSections: [],
  destination: {} as any,
  setDestination: () => {},
  testConnection: () =>
    Promise.resolve({ status: 'error' as const, data: null, message: 'Not ready' }),
  getPreparedDestination: () => null,
  isLoading: true,
  error: null,
};

const PrequelHooksContext = createContext<PrequelHooksContextState>({
  getDestinations: initialGetDestinationsState,
  useGetTransfers: () => initialGetTransfersState,
  useDestinationForm: () => initialDestinationFormState,
  useEditDestinationForm: () => initialEditDestinationFormState,
});

/**
 * Internal provider that configures the hooks
 */
const InternalHooksProvider: FC<PrequelHooksProviderProps> = ({ children }) => {
  const prequel = usePrequel();
  const { isPrequelReady } = usePrequelContext();
  const fetchPrequelAuthToken = usePrequelAuth();
  // Pre-configure the hooks when prequel is ready
  const getDestinations: GetDestinationsFunction =
    isPrequelReady && prequel.useGetDestinations
      ? prequel.useGetDestinations(fetchPrequelAuthToken, ORIGIN, PREQUEL_HOST)
      : () => Promise.resolve([]); // Return empty array when not ready

  // Pre-configure useGetTransfers with complete transfers management
  const useGetTransfers: UseGetTransfersFunction = (destinationId: string) => {
    if (!isPrequelReady || !prequel.useGetDestinations || !prequel.useGetTransfers) {
      return {
        transfers: [],
        isLoading: true,
        error: null,
      };
    }

    // Get destinations and transfers functions
    const getDestinations = prequel.useGetDestinations(fetchPrequelAuthToken, ORIGIN, PREQUEL_HOST);
    const getTransfersFn = prequel.useGetTransfers(fetchPrequelAuthToken, ORIGIN, PREQUEL_HOST);

    // State to track loading and error states
    const [transfers, setTransfers] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    // Effect to fetch destination and then transfers
    React.useEffect(() => {
      if (destinationId && isLoading && transfers.length === 0) {
        const fetchTransfers = async () => {
          try {
            setError(null);
            // First get destinations to find the one we need
            const destinations = await getDestinations();
            const destination = destinations.find((d: any) => d.id === destinationId);

            if (destination) {
              // Then get transfers for that destination
              const result = await getTransfersFn(destination);
              setTransfers(result);
              setIsLoading(false);
            } else {
              setError(`Destination with ID '${destinationId}' not found`);
              setIsLoading(false);
            }
          } catch (err) {
            setError(
              `Failed to fetch transfers: ${err instanceof Error ? err.message : 'Unknown error'}`,
            );
            setIsLoading(false);
          }
        };

        fetchTransfers();
      }
    }, [destinationId, isLoading, transfers.length, getDestinations, getTransfersFn]);

    return {
      transfers,
      isLoading,
      error,
    };
  };

  // Pre-configure useDestinationForm with complete destination management
  const useDestinationForm: UseDestinationFormFunction = (vendorName: string) => {
    if (
      !isPrequelReady ||
      !prequel.useDestination ||
      !prequel.useDestinationForm ||
      !prequel.useTestConnection ||
      !prequel.prepareDestinationWithForm
    ) {
      return {
        formSections: [],
        destination: {} as any,
        setDestination: () => {},
        testConnection: () =>
          Promise.resolve({ status: 'error', data: null, message: 'Prequel SDK not ready' }),
        getPreparedDestination: () => null,
        isLoading: true,
        error: null,
      };
    }

    // Create destination state using SDK hook
    const [destination, setDestination] = prequel.useDestination({
      name: `New ${vendorName} Example Destination`,
      vendor: vendorName,
      is_enabled: true,
      type: 0 as const, // DestinationType.Destination = 0
    });

    // Get destination form configuration
    const destinationForm = prequel.useDestinationForm(destination, PREQUEL_ORG_ID, {
      includeInternalFields: false,
      host: PREQUEL_HOST,
    });

    // Create stable function to get prepared destination on-demand
    const getPreparedDestination = React.useCallback(() => {
      return prequel.prepareDestinationWithForm(destination, destinationForm);
    }, [destination, destinationForm]);

    // Create test function that uses on-demand prepared destination
    const testConnection = React.useCallback(() => {
      const preparedDestination = getPreparedDestination();
      if (!preparedDestination) {
        return Promise.resolve({
          status: 'error' as const,
          data: null,
          message: 'Failed to prepare destination',
        });
      }

      return prequel.useTestConnection(
        fetchPrequelAuthToken,
        ORIGIN,
        PREQUEL_HOST,
      )(preparedDestination);
    }, [fetchPrequelAuthToken, getPreparedDestination, prequel]);

    return {
      formSections: destinationForm || [],
      destination,
      setDestination,
      testConnection,
      getPreparedDestination,
      isLoading: false,
      error: null,
    };
  };

  // Pre-configure useEditDestinationForm with complete destination management
  const useEditDestinationForm: UseEditDestinationFormFunction = (destinationId: string) => {
    if (
      !isPrequelReady ||
      !prequel.useDestination ||
      !prequel.useDestinationForm ||
      !prequel.useTestExistingConnection ||
      !prequel.prepareDestinationWithForm ||
      !prequel.useGetDestinations ||
      !prequel.prepareDestinationFromExisting
    ) {
      return {
        formSections: [],
        destination: {} as any,
        setDestination: () => {},
        testConnection: () =>
          Promise.resolve({ status: 'error', data: null, message: 'Prequel SDK not ready' }),
        getPreparedDestination: () => null,
        isLoading: true,
        error: null,
      };
    }

    // Create destination state for editing (starts empty)
    const [destination, setDestination] = prequel.useDestination();

    // Get destinations list to find the one we're editing
    const getDestinations = prequel.useGetDestinations(fetchPrequelAuthToken, ORIGIN, PREQUEL_HOST);

    // State to track loading and error states
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [existingDestination, setExistingDestination] = React.useState<any>(null);

    // Effect to fetch and populate existing destination data
    React.useEffect(() => {
      if (destinationId && isLoading && !existingDestination) {
        const fetchAndPopulate = async () => {
          try {
            setError(null);
            const destinations = await getDestinations();
            const found = destinations.find((d: any) => d.id === destinationId);

            if (found) {
              setExistingDestination(found);
              // Convert existing destination to editable format
              const editableDestination = prequel.prepareDestinationFromExisting(found);
              setDestination(editableDestination);
              setIsLoading(false);
            } else {
              setError(`Destination with ID '${destinationId}' not found`);
              setIsLoading(false);
            }
          } catch (err) {
            setError(
              `Failed to fetch destination: ${err instanceof Error ? err.message : 'Unknown error'}`,
            );
            setIsLoading(false);
          }
        };

        fetchAndPopulate();
      }
    }, [destinationId, isLoading, existingDestination, getDestinations, setDestination]);

    // Get destination form configuration (will be empty until destination is populated)
    const destinationForm = prequel.useDestinationForm(destination, PREQUEL_ORG_ID, {
      includeInternalFields: false,
      host: PREQUEL_HOST,
    });

    // Get test existing connection function (called at component level)
    const testExistingConnectionFn = prequel.useTestExistingConnection(
      fetchPrequelAuthToken,
      ORIGIN,
      PREQUEL_HOST,
    );

    // Create stable function to get prepared destination on-demand
    const getPreparedDestination = React.useCallback(() => {
      return prequel.prepareDestinationWithForm(destination, destinationForm);
    }, [destination, destinationForm]);

    // Create test function that uses on-demand prepared destination
    const testConnection = React.useCallback(() => {
      if (!existingDestination) {
        return Promise.resolve({
          status: 'error' as const,
          data: null,
          message: 'Existing destination not loaded yet',
        });
      }

      const preparedDestination = getPreparedDestination();
      return testExistingConnectionFn(existingDestination, preparedDestination);
    }, [existingDestination, testExistingConnectionFn, getPreparedDestination]);

    return {
      formSections: destinationForm || [],
      destination,
      setDestination,
      testConnection,
      getPreparedDestination,
      isLoading,
      error,
    };
  };

  // Note: useTestConnection and prepareDestination are now part of the unified hooks

  return (
    <PrequelHooksContext.Provider
      value={{
        getDestinations,
        useGetTransfers,
        useDestinationForm,
        useEditDestinationForm,
      }}
    >
      {children}
    </PrequelHooksContext.Provider>
  );
};

/**
 * PrequelHooksProvider - Single provider that encapsulates everything
 *
 * This provider wraps both PrequelContextProvider and hooks configuration,
 * providing a clean single-provider API for consuming components.
 *
 * Consuming components get:
 * - Pre-configured @prequel/react hooks
 * - Automatic auth token, origin, and host configuration
 * - No need to worry about loading states or null checks
 * - Consistent array return types
 */
export const PrequelHooksProvider: FC<PrequelHooksProviderProps> = ({ children }) => {
  return (
    <PrequelContextProvider>
      <InternalHooksProvider>{children}</InternalHooksProvider>
    </PrequelContextProvider>
  );
};

/**
 * Hook to access pre-configured Prequel hooks
 */
export const usePrequelHooks = () => {
  return useContext(PrequelHooksContext);
};

/**
 * Hook to get destinations - pre-configured and ready to use
 * @returns {GetDestinationsFunction} Function that returns Promise<ExistingDestination[]>
 *   - Returns empty array [] when Prequel is still loading
 *   - Returns actual destinations when ready
 */
export const useGetDestinations = (): GetDestinationsFunction => {
  const { getDestinations } = usePrequelHooks();
  return getDestinations;
};

/**
 * Hook to get transfers for a specific destination by ID
 * @param destinationId - The ID of the destination to get transfers for
 * @returns Object with transfers array, loading state, and error state
 *
 * @example
 * const { transfers, isLoading, error } = useGetTransfers('dest-123');
 */
export const useGetTransfers = (destinationId: string) => {
  const { useGetTransfers } = usePrequelHooks();
  return useGetTransfers(destinationId);
};

/**
 * Hook to get destination form fields and state management for a specific vendor
 * @param vendorName - The vendor identifier ('bigquery', 'databricks', etc.)
 * @returns Complete destination form management object
 *   - formSections: Form configuration (empty array when loading)
 *   - destination: Current destination state
 *   - setDestination: Function to update destination state
 *   - testConnection: Function to test current destination
 *   - getPreparedDestination: Function to get prepared destination object
 *
 * @example
 * const { formSections, destination, setDestination, testConnection, getPreparedDestination } = useDestinationForm('bigquery');
 *
 * // Update form field
 * const handleFieldChange = (fieldName, value) => {
 *   setDestination({ [fieldName]: value });
 * };
 *
 * // Test connection with current state
 * const handleTest = async () => {
 *   const result = await testConnection();
 *   if (result.data?.status === 'success') {
 *     console.log('Connection successful!');
 *   }
 * };
 */
export const useDestinationForm = (vendorName: VendorName): UseDestinationFormResult => {
  const { useDestinationForm: getDestinationForm } = usePrequelHooks();
  return getDestinationForm(vendorName);
};

/**
 * Hook to get destination form fields and state management for editing an existing destination
 * @param destinationId - The ID of the existing destination to edit
 * @returns Complete destination form management object
 *   - formSections: Form configuration (empty array when loading)
 *   - destination: Current destination state (pre-populated with existing data)
 *   - setDestination: Function to update destination state
 *   - testConnection: Function to test current destination
 *   - getPreparedDestination: Function to get prepared destination object
 *
 * @example
 * const { formSections, destination, setDestination, testConnection, getPreparedDestination } = useEditDestinationForm('dest-123');
 *
 * // Update form field
 * const handleFieldChange = (fieldName, value) => {
 *   setDestination({ [fieldName]: value });
 * };
 *
 * // Test connection with current state
 * const handleTest = async () => {
 *   const result = await testConnection();
 *   if (result.data?.status === 'success') {
 *     console.log('Connection successful!');
 *   }
 * };
 */
export const useEditDestinationForm = (destinationId: string): UseEditDestinationFormResult => {
  const { useEditDestinationForm: getEditDestinationForm } = usePrequelHooks();
  return getEditDestinationForm(destinationId);
};

// Note: useTestConnection is now part of the unified useDestinationForm and useEditDestinationForm hooks
